import { useEffect, useState, useContext, useMemo } from 'react';
import { useMachine } from '@xstate/react';
import { formMachine, constants } from 'lib_ui-services';
import useActiveRecord from '../useActiveRecord';
import useEventSink from '../useEventSink';
import { contexts, hooks } from 'lib_ui-primitives';
import { useTheme } from 'styled-components';
import useGetAutoFocusHNodeId from './useGetAutoFocusHNodeId';
import useSplashRecord from '../useSplashRecord';
const { useRouter } = hooks;

const { FormContext } = contexts;
const _p = {
    useRouter,
    useMachine
};
export const _private = _p;

// Debugging tools to see when form state changes (see corresponding stuff at bottom)
// import { useWhatChanged } from '@simbathesailor/use-what-changed';

const emptyErrors = { field: {}, form: [] };

/**
 * If a FormContext already exists at a higher level, this will do very little except
 * pass that existing context to the consumer.
 * @param {object} hNode
 * @returns FormContext
 */
export default function useFormMachine(hNode) {
    const router = _p.useRouter();
    const { mobile, selectedTheme } = useTheme();
    const existingFormContext = useContext(FormContext);
    const alreadyHasFormContext = Object.keys(existingFormContext).length > 0;
    const activeRecord = useActiveRecord();
    const { namespace, relation, record, type, formInstanceId } = activeRecord ?? {};
    const splashContext = useSplashRecord();
    const [errors, setErrors] = useState(emptyErrors);
    const [subscribe, publish] = useEventSink();
    const machineConfig = useMemo(() => formMachine({ selectedTheme, keepOpenAfterSubmit: hNode.keepOpenAfterSubmit }), [selectedTheme, hNode.keepOpenAfterSubmit]);
    const [state, send] = _p.useMachine(machineConfig, {
        devTools: false // __USE_XSTATE_INSPECTOR__
    });
    const isDirty = state.matches('edit.dirty');
    const isSubmitted = state.matches('submitted');

    // Ask the form machine if it is ok to login. This is used in the offlineEffectOverride
    useEffect(() => {
        return subscribe(
            {
                verb: 'confirm',
                namespace: 'application',
                relation: 'runtime',
                type: 'okToLogin'
            },
            (payload, context) => {
                const delayReason = isDirty ? 'Unsaved changes' : undefined;
                publish(
                    { isOk: !isDirty, delayReason },
                    {
                        ...context,
                        status: 'success'
                    }
                );
            }
        );
    }, [subscribe, publish, isDirty]);

    // This resets the form state machine back to the start because the activeRecord the
    // form is working on has changed.
    useEffect(() => {
        if (alreadyHasFormContext) return;
        if (isSubmitted) return; // prevents an error trying to restart a form in its final state
        const { namespace, relation, record, oldRecord, type, isNew } = activeRecord;
        let machineContext = {
            newRecord: { ...record },
            oldRecord: oldRecord || record,
            propertiesToUnset: [],
            isNew,
            publish,
            namespace,
            relation,
            type,
            formInstanceId,
            hNode,
            splashContext,
            selectedTheme,
            routePath: router?.location?.pathname
        };
        if (oldRecord) {
            send({ type: 'restartWithChanges', payload: { context: machineContext } });
        } else {
            send({ type: 'restart', payload: { context: machineContext } });
        }
    }, [
        alreadyHasFormContext,
        activeRecord,
        formInstanceId,
        publish,
        hNode,
        send,
        splashContext,
        selectedTheme,
        router?.location?.pathname,
        isSubmitted
    ]);

    // Send changes and submissions to the machine
    useEffect(() => {
        if (alreadyHasFormContext) return;
        /*
         * Standard form controls include formInstanceId in the event context. The form
         * machine copies it to change, validate, update, create, and submit events and
         * their responses. Ignore an event when its ID differs from this machine's ID,
         * because that event began in a form that is no longer displayed.
         *
         * Some callers publish these events directly without formInstanceId. Continue
         * accepting those events for compatibility; without the field, this check cannot
         * determine which displayed form produced the event.
         */
        // prettier-ignore
        const unsubscribes = [
            /* beginChange essentially passes through the change to the rules engine for proper
            merging with the form's model, but it also needs to ensure that the form state
            machine is in the correct (dirty) state as well so the UI can react properly. */
            subscribe({ verb: 'beginChange', namespace, relation, type }, (payload, originatingEventContext) => {
                if (!isForCurrentForm(formInstanceId, originatingEventContext)) return;
                send({ type: 'change', payload: { ...payload, originatingEventContext }});
            }),
            /* put the merged model state (from the rulesengine) into the machine's context state */
            subscribe({ verb: 'change', namespace, relation, type, status: 'success' }, (payload, originatingEventContext) => {
                if (!isForCurrentForm(formInstanceId, originatingEventContext)) return;
                send({ type: 'changeSuccess', payload: { ...payload, originatingEventContext }});
            }),
            subscribe({ verb: 'submit', namespace, relation, type }, (payload, originatingEventContext) => {
                if (!isForCurrentForm(formInstanceId, originatingEventContext)) return;
                setErrors(emptyErrors);
                send('submit', { ...payload, originatingEventContext });
            }),
            subscribe({ verb: 'submit', namespace, relation, type, status: 'failure' }, (payload, originatingEventContext) => {
                if (!isForCurrentForm(formInstanceId, originatingEventContext)) return;
                if (payload.errors) {
                    setErrors(payload.errors);
                }
            }),
            /* if an hNode is removed from a DynamicFieldEntry, handle deletion of field's data */
            subscribe({ verb: 'removedHNode', namespace: 'application', relation: 'runtime' }, (payload, originatingEventContext) => {
                if (!isForCurrentForm(formInstanceId, originatingEventContext)) return;
                send({ type: 'removedHNode', payload: { ...payload, originatingEventContext } });
            }),
            subscribe({ verb: 'validate', namespace, relation, type }, (payload, originatingEventContext) => {
                if (!isForCurrentForm(formInstanceId, originatingEventContext)) return;
                setErrors(emptyErrors);
                send('validate', { ...payload, originatingEventContext });
            }),
            subscribe({ verb: 'validate', namespace, relation, type, status: 'failure' }, (payload, originatingEventContext) => {
                if (!isForCurrentForm(formInstanceId, originatingEventContext)) return;
                if (payload.errors) {
                    setErrors(payload.errors);
                }
                send('validate_failure', { ...payload, originatingEventContext });
            }),
            subscribe({ verb: 'validate', namespace, relation, type, status: 'success' }, (payload, originatingEventContext) => {
                if (!isForCurrentForm(formInstanceId, originatingEventContext)) return;
                send('validate_success', { ...payload, originatingEventContext });
            }),
            subscribe({ verb: 'update', namespace, relation, type }, (payload, originatingEventContext) => {
                if (!isForCurrentForm(formInstanceId, originatingEventContext)) return;
                setErrors(emptyErrors);
                send('update', { ...payload, originatingEventContext });
            }),
            subscribe({ verb: 'update', namespace, relation, type, status: 'success' }, (payload, originatingEventContext) => {
                if (!isForCurrentForm(formInstanceId, originatingEventContext)) return;
                send('update_success', { ...payload, originatingEventContext });
            }),
            subscribe({ verb: 'update', namespace, relation, type, status: 'failure' }, (payload, originatingEventContext) => {
                if (!isForCurrentForm(formInstanceId, originatingEventContext)) return;
                send('update_failure', { ...payload, originatingEventContext });
            }),
            subscribe({ verb: 'create', namespace, relation, type, status: 'success' }, (payload, originatingEventContext) => {
                if (!isForCurrentForm(formInstanceId, originatingEventContext)) return;
                send('create_success', { ...payload, originatingEventContext });
            }),
            subscribe({ verb: 'create', namespace, relation, type, status: 'failure' }, (payload, originatingEventContext) => {
                if (!isForCurrentForm(formInstanceId, originatingEventContext)) return;
                send('create_failure', { ...payload, originatingEventContext });
            })
        ];
        // Return a cleanup function for the unmount
        return () => {
            unsubscribes.forEach(u => u());
        };
    }, [alreadyHasFormContext, namespace, relation, type, formInstanceId, send, subscribe]);

    useEffect(() => {
        if (alreadyHasFormContext) return;
        return subscribe({ verb: 'confirm', namespace, relation, type: 'changeRecord' }, async (payload, originatingEventContext) => {
            // Confirm with the user if abandoning changes.
            if (
                // despite appearances and trying it to work otherwise `doingCreate_sensor_tag_it.js` puts `ignoreChanges`
                // in payload.newRecord while other places succeed putting it into the root. So check both places:
                !payload.ignoreChanges &&
                !payload.newRecord?.ignoreChanges &&
                state.matches('edit.dirty') &&
                !['submit', 'remove'].includes(payload.changeContext?.verb ?? [])
            ) {
                return new Promise((resolve, reject) => {
                    publish(
                        {
                            jumpQueue: true,
                            message: 'Are you sure you want to cancel without saving?',
                            okButtonText: 'YES',
                            cancelButtonText: 'NO',
                            variant: constants.VARIANT.Warning,
                            okAction: () => {
                                publish(payload, {
                                    ...originatingEventContext,
                                    status: 'success'
                                });
                                resolve();
                            },
                            cancelAction: () => {
                                publish(
                                    {
                                        ...payload,
                                        errors: { field: {}, form: [] }
                                    },
                                    {
                                        ...originatingEventContext,
                                        status: 'failure'
                                    }
                                );
                                reject('The user request to cancel without saving changed data was reconsidered.');
                            }
                        },
                        {
                            verb: 'confirm',
                            namespace: 'application',
                            relation: 'user'
                        }
                    );
                });
            }
            // If the form is not dirty or the user is actually submitting or deleting the record,
            // then proceed without confirmation.
            publish(payload, {
                ...originatingEventContext,
                status: 'success'
            });
            return Promise.resolve();
        });
    }, [alreadyHasFormContext, subscribe, state, publish, namespace, relation, selectedTheme]);

    const autoFocusHNodeId = useGetAutoFocusHNodeId(
        hNode,
        state.context.newRecord,
        mobile,
        activeRecord?.isNew,
        isDirty
    );

    const disabled = state.matches('view') || state.matches('edit.dirty.submitting');
    const submitting = state.matches('edit.dirty.submitting');

    const formContext = useMemo(() => {
        return {
            isDirty,
            submitting,
            disabled,
            oldRecord: record,
            newRecord: state.context.newRecord,
            formErrors: errors.form,
            errorsByPath: errors.field,
            state,
            autoFocusHNodeId
        };
    }, [
        isDirty,
        submitting,
        disabled,
        record,
        state,
        errors.form,
        errors.field,
        autoFocusHNodeId
    ]);

    if (alreadyHasFormContext) return existingFormContext;

    // Debugging tools to see when the form state changes:
    // useWhatChanged(
    //     [
    //         result.isDirty,
    //         result.submitting,
    //         result.disabled,
    //         result.oldRecord,
    //         result.newRecord,
    //         result.formErrors,
    //         result.errorsByPath,
    //         result.autoFocusHNodeId
    //     ],
    //     'isDirty, submitting, disabled, oldRecord, newRecord, formErrors, errorsByPath, autoFocusHNodeId'
    // );

    return formContext;
}

/**
 * Reports whether an event may update this form machine.
 *
 * An event with a formInstanceId is accepted only when that ID matches this form
 * machine. Events without formInstanceId are accepted because some callers still
 * publish form events directly without the field.
 *
 * @param {string} formInstanceId - The identity of this form machine.
 * @param {object} eventContext - Context published with the form event.
 * @returns {boolean}
 */
function isForCurrentForm(formInstanceId, eventContext) {
    return eventContext?.formInstanceId == null || eventContext.formInstanceId === formInstanceId;
}
